import { create } from 'zustand';
import { getBookingPolicies } from '../axios/bookingPolicies';
import { normalizeFlags } from '../flow/flowFlags';

/**
 * Each location's saved booking-flow flags (Settings > Appointment >
 * Appointment Journey), cached so the sidebar can show Categories /
 * Professionals only when they're enabled. Loaded once per location from
 * `GET /location/findById/:id`; whoever saves new flags calls `setFlags` so
 * the menu updates straight away.
 */
export const useBookingFlagsStore = create((set, get) => ({
  /** @type {Object.<string, import('../flow/types').BookingFlowFlags>} */
  flagsByLocation: {},

  /** Fetches a location's flags unless already cached (or `force`). Never throws. */
  async loadFlags(locationId, { force = false } = {}) {
    if (!locationId) return;
    const key = String(locationId);
    if (!force && get().flagsByLocation[key]) return;
    try {
      const response = await getBookingPolicies(locationId);
      const location = response?.data?.data;
      if (!location) return;
      get().setFlags(locationId, {
        serviceOnly: location.serviceOnly ?? true,
        isCategoryEnabled: location.isCategoryEnabled ?? false,
        areProfessionalsEnabled: location.areProfessionalsEnabled ?? false,
        professionalFirst: location.professionalFirst ?? false,
        profAndSerNoAssociation: location.profAndSerNoAssociation ?? false
      });
    } catch {
      // Menu items stay hidden until a later load succeeds.
    }
  },

  setFlags(locationId, flags) {
    set((state) => ({
      flagsByLocation: { ...state.flagsByLocation, [String(locationId)]: normalizeFlags(flags) }
    }));
  }
}));
